import { useMemo } from 'react';
import { useLanguagePack } from '@americanexpress/one-app-hooks';
import { useModule } from '@americanexpress/holocron-hooks';
import type { OneAppModule } from 'one-app-types';
import useLinks from './useLinks';

export type UseModuleDataResponse = {
  loading: Record<string, boolean>,
  data: {
    lang?: Record<string, string>;
    links?: SiteAreaNavLinks;
    ConsumerContextSwitcher?: OneAppModule<ConsumerContextSwitcherProps>;
    SiteAreaNavContainer?: OneAppModule<SiteAreaNavContainerProps>;
    MycaSiteAreaNav?: OneAppModule;
  },
  error: Record<string, Error | undefined>,
};

const useModuleData = (
  props: ConsumerNavigationPropsFull,
  { isConsumerNavEligible }: { isConsumerNavEligible: boolean }
): UseModuleDataResponse => {
  const { view } = props;
  const isProductView = view === 'PRODUCT';

  const langResponse = useLanguagePack('axp-consumer-navigation', {
    defer: !isConsumerNavEligible,
  });
  const linksResponse = useLinks(props, isConsumerNavEligible);
  const consumerContextSwitcherResponse = useModule('axp-consumer-context-switcher', {
    defer: !isConsumerNavEligible,
  });
  const siteAreaNavContainerResponse = useModule('axp-site-area-nav-container', {
    defer: !isConsumerNavEligible,
  });
  const mycaSanModuleResponse = useModule('axp-myca-site-area-nav', {
    defer: isConsumerNavEligible || !isProductView,
  });

  return useMemo(() => {
    const responseMap = {
      lang: langResponse,
      links: linksResponse,
      ConsumerContextSwitcher: consumerContextSwitcherResponse,
      SiteAreaNavContainer: siteAreaNavContainerResponse,
      MycaSiteAreaNav: mycaSanModuleResponse,
    };

    return {
      // return
      loading: Object.fromEntries(
        Object.entries(responseMap).map(([key, { isLoading }]) => [key, isLoading])
      ),
      // return data by the mapped key
      data: Object.fromEntries(
        Object.entries(responseMap).map(([key, { data }]) => [key, data])
      ),
      // return error by the mapped key
      error: Object.fromEntries(
        Object.entries(responseMap).map(([key, { error }]) => [key, error])
      ),
    };
  }, [
    langResponse,
    linksResponse,
    consumerContextSwitcherResponse,
    siteAreaNavContainerResponse,
    mycaSanModuleResponse,
  ]);
};

export default useModuleData;
